// The Analyses filter row (DESIGN_SPEC §11.3): one row that wraps.
//   <FilterBar q onQuery filters counts profiles onChange searchRef />
//     search (up to 300 px, gives way first; "/" focuses it, Esc clears) · state/verdict chips with counts (the
//     pressed one gets the rule-strong box; glyphs are SVG) · profile, network and kind selects at the right
//   q is the live search text (the URL copy is debounced by the page); onChange receives the next filters;
//   counts are left out until the list has loaded
import type { ReactNode, Ref } from "react";
import { Chip, ChipGroup, Pip, Search, Select, VerdictGlyph, type SelectOption } from "@/components/ui";
import { CHIPS, pressedChip, withChip, type ChipKey, type Filters } from "./filters";

const NETWORKS: readonly SelectOption<string>[] = [
  { value: "", label: "any network" },
  { value: "isolated", label: "isolated" },
  { value: "simulated", label: "simulated" },
  { value: "internet", label: "internet" },
];

const KINDS: readonly SelectOption<string>[] = [
  { value: "", label: "any kind" },
  { value: "file", label: "files" },
  { value: "url", label: "URLs" },
];

function chipGlyph(key: ChipKey): ReactNode {
  if (key === "live") return <Pip state="still" />;
  if (key === "malicious" || key === "suspicious" || key === "benign")
    return (
      <span className={`v v--${key}`} aria-hidden="true">
        <VerdictGlyph v={key} />
      </span>
    );
  return null;
}

export function FilterBar({
  q,
  onQuery,
  filters,
  counts,
  profiles,
  onChange,
  searchRef,
}: {
  q: string;
  onQuery: (q: string) => void;
  filters: Filters;
  counts?: Record<ChipKey, number>;
  profiles: readonly string[];
  onChange: (next: Filters) => void;
  searchRef?: Ref<HTMLInputElement>;
}) {
  const pressed = pressedChip(filters);
  const profileOptions: SelectOption<string>[] = [{ value: "", label: "any profile" }, ...profiles.map((p) => ({ value: p, label: p }))];
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-rule-cell pt-4 pb-3">
      <Search
        value={q}
        onChange={onQuery}
        label="Search analyses by name, SHA-256, label or tag"
        placeholder="name, sha-256, label or tag"
        inputRef={searchRef}
        className="w-full min-[720px]:w-auto min-[720px]:max-w-[300px] min-[720px]:flex-[1_1_220px]"
      />
      <ChipGroup label="Show runs">
        {CHIPS.map((c) => (
          <Chip
            key={c.key}
            pressed={pressed === c.key}
            all={c.key === "all"}
            glyph={chipGlyph(c.key)}
            count={counts?.[c.key]}
            className="chip--pick"
            onClick={() => onChange(withChip(filters, c.key))}
          >
            {c.label}
          </Chip>
        ))}
      </ChipGroup>
      <div className="flex flex-wrap items-center gap-2 min-[720px]:ml-auto">
        <Select label="Profile" value={filters.profile} options={profileOptions} onChange={(profile) => onChange({ ...filters, profile })} />
        <Select label="Network" value={filters.network} options={NETWORKS} onChange={(network) => onChange({ ...filters, network })} />
        <Select label="Kind" value={filters.kind} options={KINDS} onChange={(kind) => onChange({ ...filters, kind })} />
      </div>
    </div>
  );
}
